// @flow
import type { JwtToken, UserData } from '../../types';
import api from '../../services/api';
import LocalStorageService from '../../services/localStorageService';
import configureSentryScope from '../sentry/configureSentryScope';
import decode from 'jwt-decode';
import { getClaims } from './grantAccess';

type ReturnType = {
  success: boolean,
  userName: string,
  clockNumber: number,
  costCentre: string,
  department: ?string,
  imageUrl: string,
  roles: Array<string>,
  requiresPasswordReset: boolean
};

const isTokenExpired = (decodedToken: JwtToken): boolean =>
  decodedToken.exp < Date.now() / 1000;

export default function validateToken(): Promise<ReturnType> {
  return new Promise((resolve, reject) => {
    const token = LocalStorageService.getToken();

    if (!token) {
      return resolve({ success: false });
    }

    let decodedToken: JwtToken;

    try {
      decodedToken = decode(token);
    } catch {
      LocalStorageService.removeToken();
      return resolve({ success: false });
    }

    if (isTokenExpired(decodedToken)) {
      LocalStorageService.removeToken();
      return resolve({ success: false });
    }

    const clockNumber = +decodedToken.sub;

    api.employee
      .get(clockNumber)
      .then(response => {
        const userData: UserData = {
          ...response.data,
          roles:
            decodedToken[
              'http://schemas.microsoft.com/ws/2008/06/identity/claims/role'
            ],
          claims: getClaims(decodedToken)
        };

        configureSentryScope(userData.userName);

        resolve({
          success: true,
          ...userData
        });
      })
      .catch(() => {
        LocalStorageService.removeToken();
        reject(() => reject());
      });
  });
}
